// AGC: nativeGeolocation.ts
// AGC: Unified geolocation service for Droidfarm.
// AGC: Uses @capacitor/geolocation on native Android/iOS for native GPS chip access.
// AGC: Falls back to browser navigator.geolocation on web (development / PWA).
//
// AGC: Permission management is delegated to permissionHelper.ts which implements
// AGC: the correct check -> request -> verify -> settings-fallback flow, including:
//   - Android 12+ approximate vs precise location
//   - Blocked permission detection (don't ask again)
//   - Safe handling of Capacitor plugin throws without optimistic false returns
//
// AGC: Note on background location:
// AGC: watchPosition works reliably in the foreground only.
// AGC: Background GPS (ACCESS_BACKGROUND_LOCATION + foreground service) is intentionally
// AGC: NOT implemented here -- all current use cases (field mapping, note geotagging,
// AGC: weather) are foreground-only.

import { Capacitor } from '@capacitor/core'
import { Geolocation, type Position } from '@capacitor/geolocation'
import { ensurePermission, PermissionBlockedError } from './permissionHelper'

// AGC: Re-export PermissionBlockedError so callers only need to import from nativeGeolocation
export { PermissionBlockedError } from './permissionHelper'

// AGC: Normalized coords type -- matches what both native and browser return
export interface GeoCoords {
  latitude: number
  longitude: number
  accuracy: number | null
  altitude: number | null
  altitudeAccuracy: number | null
  heading: number | null
  speed: number | null
}

// AGC: Options for position requests
export interface GeoOptions {
  enableHighAccuracy?: boolean
  timeout?: number
  maximumAge?: number
}

// AGC: Default options optimized for field-mapping use cases
const DEFAULT_OPTIONS: GeoOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 5000,
}

// AGC: Check if running on native Android or iOS
const isNative = (): boolean => Capacitor.isNativePlatform()

/**
 * AGC: Get the current device position (one-shot).
 *
 * AGC: Flow on native Android:
 *   1. Try getCurrentPosition() directly (fast path -- no dialog if already granted).
 *   2. On permission error: call permissionHelper.ensurePermission('location').
 *      - blocked (don't ask again) -> throw PermissionBlockedError (callers show modal)
 *      - coarse-only (Android 12+ approximate) -> retry with enableHighAccuracy=false
 *      - granted -> retry with original options
 *      - still not granted -> throw PermissionBlockedError
 *   3. On timeout/unavailable: throw immediately with translated message.
 *
 * AGC: On web: uses navigator.geolocation (browser API).
 * AGC: Throws PermissionBlockedError when permission is permanently denied.
 * AGC: Throws Error with Portuguese message for other failures.
 */
export const getCurrentPosition = async (
  options: GeoOptions = DEFAULT_OPTIONS
): Promise<GeoCoords> => {
  if (isNative()) {
    // AGC: Fast path -- try directly first. Avoids permission dialog for already-granted state.
    try {
      const pos: Position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 5000,
      })
      return extractCoords(pos)
    } catch (firstErr: any) {
      const firstMsg = firstErr?.message || String(firstErr)
      console.warn('AGC: nativeGeolocation: first attempt failed:', firstMsg)

      // AGC: If this is NOT a permission error, throw immediately with translated message
      if (!isPermissionError(firstMsg)) {
        throw new Error(translateGeoError(firstErr))
      }

      // AGC: Permission error -- run the full check -> request -> verify flow
      const result = await ensurePermission('location')

      if (result.blocked || !result.granted) {
        // AGC: Permanently blocked or denied -- throw typed error so callers show modal
        throw new PermissionBlockedError('location')
      }

      // AGC: Android 12+ coarse-only: retry with low accuracy
      const useHighAccuracy = result.precise ? (options.enableHighAccuracy ?? true) : false

      if (!result.precise) {
        console.log(
          'AGC: nativeGeolocation: coarse-only location (Android 12+ approximate) -- retrying with enableHighAccuracy=false'
        )
      }

      // AGC: Retry with appropriate accuracy setting
      try {
        const pos: Position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: useHighAccuracy,
          timeout: options.timeout ?? 10000,
          maximumAge: options.maximumAge ?? 5000,
        })
        return extractCoords(pos)
      } catch (retryErr: any) {
        console.error('AGC: nativeGeolocation: retry after permission grant failed:', retryErr)

        // AGC: If retry also fails with permission error, it's now truly blocked
        const retryMsg = retryErr?.message || String(retryErr)
        if (isPermissionError(retryMsg)) {
          throw new PermissionBlockedError('location')
        }
        throw new Error(translateGeoError(retryErr))
      }
    }
  }

  // AGC: Web fallback -- use browser navigator.geolocation
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalização não suportada neste navegador'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(extractBrowserCoords(pos)),
      (err) => reject(new Error(translateBrowserGeoError(err))),
      {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 5000,
      }
    )
  })
}

/**
 * AGC: Watch position -- continuously streams location updates (foreground only).
 * AGC: On native: uses Capacitor Geolocation watchPosition.
 * AGC: On web: uses navigator.geolocation.watchPosition.
 * AGC: Returns a watch ID -- pass to clearWatch() to stop tracking.
 *
 * AGC: IMPORTANT: This works reliably only while the app is in the foreground.
 * AGC: Background location tracking is out of scope (requires foreground service).
 */
export const watchPosition = (
  callback: (coords: GeoCoords | null, error: string | null) => void,
  options: GeoOptions = DEFAULT_OPTIONS
): Promise<string | number> => {
  if (isNative()) {
    return Geolocation.watchPosition(
      {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 5000,
      },
      (pos, err) => {
        if (err) {
          callback(null, translateGeoError(err))
        } else if (pos) {
          callback(extractCoords(pos), null)
        }
      }
    )
  }

  // AGC: Web fallback
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      callback(null, 'Geolocalização não suportada neste navegador')
      resolve(-1)
      return
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => callback(extractBrowserCoords(pos), null),
      (err) => callback(null, translateBrowserGeoError(err)),
      {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 5000,
      }
    )
    resolve(id)
  })
}

/**
 * AGC: Stop watching position.
 * AGC: Pass the ID returned by watchPosition().
 */
export const clearWatch = async (watchId: string | number): Promise<void> => {
  if (isNative()) {
    await Geolocation.clearWatch({ id: String(watchId) })
  } else {
    if (typeof watchId === 'number' && watchId !== -1) {
      navigator.geolocation.clearWatch(watchId)
    }
  }
}

// =============================================================================
// AGC: Internal helpers
// =============================================================================

/** AGC: Detect if an error message represents a permission-related failure */
const isPermissionError = (msg: string): boolean => {
  const lower = msg.toLowerCase()
  return (
    lower.includes('denied') ||
    lower.includes('permission') ||
    lower.includes('not allowed') ||
    lower.includes('user denied')
  )
}

/** AGC: Extract coords from a Capacitor Position object */
const extractCoords = (pos: Position): GeoCoords => ({
  latitude: pos.coords.latitude,
  longitude: pos.coords.longitude,
  accuracy: pos.coords.accuracy ?? null,
  altitude: pos.coords.altitude ?? null,
  altitudeAccuracy: pos.coords.altitudeAccuracy ?? null,
  heading: pos.coords.heading ?? null,
  speed: pos.coords.speed ?? null,
})

/** AGC: Extract coords from a browser GeolocationPosition object */
const extractBrowserCoords = (pos: GeolocationPosition): GeoCoords => ({
  latitude: pos.coords.latitude,
  longitude: pos.coords.longitude,
  accuracy: pos.coords.accuracy,
  altitude: pos.coords.altitude ?? null,
  altitudeAccuracy: pos.coords.altitudeAccuracy ?? null,
  heading: pos.coords.heading ?? null,
  speed: pos.coords.speed ?? null,
})

/** AGC: Translate native Capacitor geolocation errors to user-friendly Portuguese messages */
const translateGeoError = (error: any): string => {
  const msg = error?.message || String(error)
  if (isPermissionError(msg)) {
    return 'Permissão de localização negada. Habilite nas configurações do dispositivo.'
  }
  if (msg.includes('unavailable') || msg.includes('POSITION_UNAVAILABLE')) {
    return 'Localização indisponível. Verifique se o GPS está habilitado.'
  }
  if (msg.includes('timeout') || msg.includes('TIMEOUT')) {
    return 'Tempo esgotado ao obter localização. Tente novamente.'
  }
  return 'Erro ao obter localização: ' + msg
}

/** AGC: Translate browser GeolocationPositionError codes to user-friendly messages */
const translateBrowserGeoError = (err: GeolocationPositionError): string => {
  switch (err.code) {
    case GeolocationPositionError.PERMISSION_DENIED:
      return 'Permissão de localização negada pelo navegador.'
    case GeolocationPositionError.POSITION_UNAVAILABLE:
      return 'Localização indisponível. Verifique se o GPS está habilitado.'
    case GeolocationPositionError.TIMEOUT:
      return 'Tempo esgotado ao obter localização. Tente novamente.'
    default:
      return 'Erro ao obter localização: ' + err.message
  }
}
